import React, { useState } from "react";
import { Navigate, useLocation, Link } from "react-router-dom";
import { LogIn, Lock, Mail, ArrowLeft } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { BRAND } from "@/lib/brand";

export default function Login() {
  const { user, login } = useAuth();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to={user.role === "freelance_worker" ? "/my" : location.state?.from || "/dashboard"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(email.trim(), password);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail, err.message));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen workshop-bg text-slate-100 flex items-center justify-center px-4 relative">
      <Link to="/" data-testid="login-back-to-site" className="absolute top-4 left-4 sm:top-6 sm:left-6 inline-flex items-center gap-2 h-10 px-3.5 rounded-full border border-slate-700 bg-slate-900/70 backdrop-blur text-sm font-semibold text-slate-300 hover:text-amber-300 hover:border-amber-500/40 hover:bg-slate-800/80 transition-[color,background-color,border-color]">
        <ArrowLeft className="w-4 h-4" /> Back to website <span className="font-zh text-xs text-slate-500">返回网站</span>
      </Link>
      <div className="w-full max-w-md pt-16 sm:pt-0" data-testid="login-page">
        <div className="flex items-center gap-4 mb-8">
          <div className="w-16 h-16 rounded-2xl bg-white grid place-items-center shadow-xl shadow-sky-900/40 ring-1 ring-sky-400/30 shrink-0">
            <img src={BRAND.emblem} alt="" className="w-14 h-14 object-contain" />
          </div>
          <div className="min-w-0">
            <h1 className="font-heading text-xl sm:text-2xl font-black leading-tight">{BRAND.fullName}</h1>
            <p className="font-zh text-xs text-sky-300/80 tracking-wide">{BRAND.tagline}</p>
          </div>
        </div>

        <form onSubmit={submit} className="bg-[#151D2E] rounded-2xl border border-slate-800 p-6 sm:p-8 space-y-5 shadow-2xl shadow-black/40">
          <div>
            <p className="text-xs uppercase tracking-widest text-amber-400 font-semibold">Staff Login · 员工登录</p>
            <h2 className="font-heading text-2xl font-bold mt-1">Sign in to continue</h2>
            <p className="text-sm text-slate-400 mt-1">Customers, quotes and prices are protected.</p>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Email</Label>
            <div className="relative">
              <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <Input type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email-input" className="bg-slate-950/60 border-slate-700 h-11 pl-9" placeholder="you@hupleyhuat.sg" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Password</Label>
            <div className="relative">
              <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <Input type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password-input" className="bg-slate-950/60 border-slate-700 h-11 pl-9" placeholder="••••••••" />
            </div>
          </div>
          {error && <p className="text-sm text-rose-300 bg-rose-950/40 border border-rose-900/60 rounded-lg px-3 py-2" data-testid="login-error">{error}</p>}
          <Button type="submit" disabled={busy} data-testid="login-submit-btn" className="w-full h-11 bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-base">
            <LogIn className="w-4 h-4 mr-2" /> {busy ? "Signing in…" : "Sign in"}
          </Button>
          <p className="text-[11px] text-slate-500 text-center">Stays signed in on this device for 72 hours of inactivity.</p>
        </form>
        <p className="text-center text-xs text-slate-500 mt-4">Freelance spray painter? <Link to="/worker-login" data-testid="login-worker-link" className="text-teal-300 hover:underline underline-offset-2 font-semibold">Worker login</Link></p>
      </div>
    </div>
  );
}
